import { useMemo, useRef, type ChangeEvent, type UIEvent } from 'react';

import '../styles/custom-code-editor.css';
import { highlightCustomCodeContent, type CustomCodeHighlightMode } from '../utils/customCodeHighlight';

type Props = {
  value: string;
  onChange: (nextValue: string) => void;
  mode: CustomCodeHighlightMode;
  disabled?: boolean;
  placeholder?: string;
};

export default function CustomCodeEditor({
  value,
  onChange,
  mode,
  disabled = false,
  placeholder = 'Section is empty'
}: Props) {
  const highlightRef = useRef<HTMLPreElement>(null);

  const highlightedHtml = useMemo(
    () => highlightCustomCodeContent(value, mode),
    [mode, value]
  );

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  const handleScroll = (event: UIEvent<HTMLTextAreaElement>) => {
    if (!highlightRef.current) {
      return;
    }

    highlightRef.current.scrollTop = event.currentTarget.scrollTop;
    highlightRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  return (
    <div className={`custom-code-editor ${disabled ? 'custom-code-editor--disabled' : ''}`}>
      <pre
        ref={highlightRef}
        aria-hidden="true"
        className="custom-code-editor__highlight"
      >
        <code dangerouslySetInnerHTML={{ __html: highlightedHtml }} />
      </pre>
      <textarea
        aria-label={mode === 'css' ? 'CSS code editor' : (mode === 'js' ? 'JavaScript code editor' : 'HTML code editor')}
        value={value}
        onChange={handleChange}
        onScroll={handleScroll}
        disabled={disabled}
        spellCheck={false}
        placeholder={placeholder}
        className="custom-code-editor__input"
      />
    </div>
  );
}
